import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  SelectChangeEvent
} from '@mui/material';

interface Certificate {
  id: string;
  certificate_type: string;
  issue_date: string;
  expiry_date: string;
  property_address: string;
  original_filename?: string;
  normalized_address?: string;
  inspector_name?: string;
  status?: string;
}

interface ReminderEditDialogProps {
  open: boolean;
  onClose: () => void;
  certificate: Certificate | null;
  onSave: (reminderText: string, daysBeforeExpiry: number, reminderTime: string) => void;
  onDelete?: () => void;
  existingReminder?: {
    text: string;
    daysBeforeExpiry: number;
    reminderId?: string;
    reminderTime?: string;
    reminderDate?: string; // actual stored date from DB (YYYY-MM-DD)
  };
}

const ReminderEditDialog: React.FC<ReminderEditDialogProps> = ({
  open,
  onClose,
  certificate,
  onSave,
  onDelete,
  existingReminder
}) => {
  const [reminderText, setReminderText] = useState('');
  const [daysBeforeExpiry, setDaysBeforeExpiry] = useState<number>(7);
  const [reminderOption, setReminderOption] = useState<string>('14'); // '14', '21', or 'custom'
  const [customDate, setCustomDate] = useState<string>('');
  const [reminderTime, setReminderTime] = useState<string>('09:30'); // Default 9:30 AM

  useEffect(() => {
    if (existingReminder) {
      setReminderText(existingReminder.text);
      const days = existingReminder.daysBeforeExpiry;
      setDaysBeforeExpiry(days);
      setReminderTime(existingReminder.reminderTime || '09:30');
      
      // Set the appropriate option based on days
      if (days === 14) {
        setReminderOption('14');
      } else if (days === 21) {
        setReminderOption('21');
      } else {
        setReminderOption('custom');
        // Use the actual stored reminder_date from DB if available,
        // otherwise fall back to recalculating from daysBeforeExpiry
        if (existingReminder.reminderDate) {
          setCustomDate(existingReminder.reminderDate);
        } else if (certificate) {
          const expiryDate = new Date(certificate.expiry_date);
          expiryDate.setDate(expiryDate.getDate() - days);
          setCustomDate(expiryDate.toISOString().split('T')[0]);
        }
      }
    } else if (certificate) {
      // Default reminder text based on certificate type
      const defaultText = `${certificate.certificate_type} certificate expires on ${certificate.expiry_date}`;
      setReminderText(defaultText);
      setDaysBeforeExpiry(21); // Default to 3 weeks
      setReminderOption('21');
      setReminderTime('09:30'); // Default to 9:30 AM
      setCustomDate(certificate.expiry_date); // Start custom picker at expiry date
    }
  }, [existingReminder, certificate, open]);

  const handleSave = () => {
    if (reminderText.trim()) {
      onSave(reminderText.trim(), daysBeforeExpiry, reminderTime);
      onClose();
    }
  };

  const handleOptionChange = (event: SelectChangeEvent<string>) => {
    const option = event.target.value;
    setReminderOption(option);
    
    if (option === '14') {
      setDaysBeforeExpiry(14);
    } else if (option === '21') {
      setDaysBeforeExpiry(21);
    } else if (option === 'custom') {
      // Keep current days or default to expiry date
      if (certificate) {
        setCustomDate(certificate.expiry_date);
      }
    }
  };

  const handleCustomDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedDate = event.target.value;
    setCustomDate(selectedDate);
    
    // Calculate days before expiry
    if (certificate && selectedDate) {
      const expiryDate = new Date(certificate.expiry_date);
      const reminderDate = new Date(selectedDate);
      const diffTime = expiryDate.getTime() - reminderDate.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      setDaysBeforeExpiry(diffDays > 0 ? diffDays : 1);
    }
  };
  
  // Get today's date in YYYY-MM-DD format for min date validation
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  if (!certificate) {
    return null;
  }

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ textAlign: 'center', fontWeight: 600, fontSize: '1.5rem' }}>
        {existingReminder ? 'Change Reminder' : 'Set Reminder'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Certificate expires on: <strong>{certificate.expiry_date}</strong>
          </Typography>
          
          <TextField
            fullWidth
            label="Reminder Message"
            multiline
            rows={3}
            value={reminderText}
            onChange={(e) => setReminderText(e.target.value)}
            placeholder="Enter the message for your reminder..."
            sx={{ mb: 3 }}
          />

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Remind me before expiry</InputLabel>
            <Select
              value={reminderOption}
              label="Remind me before expiry"
              onChange={handleOptionChange}
            >
              <MenuItem value="14">2 Weeks</MenuItem>
              <MenuItem value="21">3 Weeks</MenuItem>
              <MenuItem value="custom">Custom</MenuItem>
            </Select>
          </FormControl>

          {reminderOption === 'custom' && (
            <TextField
              fullWidth
              type="date"
              label="Custom Reminder Date"
              value={customDate}
              onChange={handleCustomDateChange}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                min: getTodayDate(), // Can't set reminder in the past
                max: certificate.expiry_date // Can't set reminder after expiry
              }}
              sx={{ mb: 2 }}
            />
          )}

          <TextField
            fullWidth
            type="time"
            label="Reminder Time"
            value={reminderTime}
            onChange={(e) => setReminderTime(e.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
            helperText="Calendar event and email will be scheduled at this time"
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: existingReminder && onDelete ? 'space-between' : 'flex-end' }}>
        {existingReminder && onDelete && (
          <Button 
            onClick={onDelete}
            color="error"
            variant="outlined"
          >
            Delete Reminder
          </Button>
        )}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleSave}
            variant="contained"
            disabled={!reminderText.trim()}
          >
            {existingReminder ? 'Update Reminder' : 'Set Reminder'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ReminderEditDialog;